/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-08 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Card, Button, TextContainer, TextStyle, Heading, ProgressBar } from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import {
  SYNC_TYPE_FLOW_MANUAL_SYNC,
  SYNC_PROCESS_STATUSES,
  getSyncContent
} from 'src/constants/sync';
import * as syncActions from 'actions/sync-actions';
import { site } from 'src/config/';
import { formatDatetime } from 'src/helpers';
import { formatSecondsToMinutesOrHours, getTimeConvert } from 'src/helpers/time-helper';
// import { syncStep } from 'src/constants/sync';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import SuccessIcon from 'src/assets/images/sync/success.svg';
import syncLoadingIcon from 'src/assets/images/dashboard-v3/sync-gif.gif';

type Props = {
  manualSyncLoading: boolean,
  error: any,
  lastSyncedDoneAt: number,
  tenantId: string,
  getSyncStatus: (tenantId: string) => void,
  resetSyncStatus: () => void,
  syncProcessLoading: any,
  installSyncStatus: string,
  installSyncProgress: number,
  flow: any,
  getSyncProcessStatus: any,
  availableSyncList: any
};

type State = {
  syncType: any,
  showProgress: boolean,
  triggerSync: boolean,
  percentage: number,
  triggerSyncFlag: boolean,
  syncProcessDot: string
};

type ComposedProps = Props & WithI18nProps;
// NOT DONE TRANSLATION - WONT BE ABLE TO TEST - UPCOMING UPDATES BTW
class SyncHookStatus extends Component<ComposedProps, State> {
  timeoutGetSyncStatus = undefined;

  state = {
    syncType: SYNC_TYPE_FLOW_MANUAL_SYNC,
    showProgress: false,
    triggerSync: false,
    percentage: 0,
    triggerSyncFlag: false,
    syncProcessDot: ''
  };

  componentDidMount() {
    const { getSyncStatus, tenantId, getSyncProcessStatus } = this.props;
    getSyncStatus(tenantId);
    getSyncProcessStatus();
  }

  componentWillReceiveProps(nextProps: Props) {
    const { syncProcessLoading, installSyncStatus, installSyncProgress, getSyncProcessStatus } =
      nextProps;
    const { syncProcessLoading: syncProcessLoadingPrev, installSyncStatus: installSyncStatusPrev } =
      this.props;

    // check installation status
    if (installSyncStatus === 'running') {
      this.setState({ showProgress: true, percentage: parseFloat(installSyncProgress * 100) });
    } else if (installSyncStatus === 'done' && installSyncStatusPrev === 'running') {
      this.setState({ showProgress: false, percentage: 0, triggerSyncFlag: true });
      getSyncProcessStatus(SYNC_TYPE_FLOW_MANUAL_SYNC);
    }
    // Check sync process
    else if (syncProcessLoadingPrev && !syncProcessLoading) {
      const { flow, availableSyncList } = nextProps;
      const { status, completedPercent = 0 } = flow;
      const { showProgress, syncType, triggerSyncFlag } = this.state;
      // Status = "done"
      if (status === 'done') {
        // showProgress = false: Do nothing, show button trigger
        // showProgress = true: Reset progress
        if (showProgress) {
          // Update percentage
          const percentage = parseFloat(completedPercent * 100);
          if (!Number.isNaN(percentage)) {
            this.setState({ percentage });
          }

          // Clear time out interval
          clearInterval(this.intervalProgressDot);

          // Set time out 10s: showProgress = false
          setTimeout(() => {
            this.setState({ percentage: 0, showProgress: false });
          }, site.showSyncProgressTimeout);
        }

        // Update trigger sync flag
        if (triggerSyncFlag) {
          this.setState({ triggerSyncFlag: false });
        }
        // Update syncType if needed
        if (_.isEmpty(syncType) && !_.isEmpty(availableSyncList)) {
          this.setState({ syncType: SYNC_TYPE_FLOW_MANUAL_SYNC });
        }
      } else {
        // Status = "running" ||"error" || anything else --> show progress
        // Check to show progress
        if (!showProgress) {
          this.setState({
            showProgress: true
          });
        }

        // Update percentage
        const percentage = parseFloat(completedPercent * 100);
        if (!Number.isNaN(percentage)) {
          this.setState({ percentage });
        }

        // Set interval syncProcessDot
        if (this.intervalProgressDot === undefined) {
          this.intervalProgressDot = setInterval(() => {
            let { syncProcessDot } = this.state;
            syncProcessDot = syncProcessDot === '...' ? '.' : syncProcessDot + '.';
            this.setState({ syncProcessDot });
          }, 500);
        }

        // Update sync type
        const { flowName } = flow;
        if (syncType !== flowName && !_.isEmpty(availableSyncList)) {
          if (availableSyncList.includes(flowName)) {
            this.setState({ syncType: flowName });
          } else {
            this.setState({ syncType: SYNC_TYPE_FLOW_MANUAL_SYNC });
          }
        }
      }

      // Set time out to get sync status -- Always run
      this.timeoutGetSyncProcessStatus = setTimeout(() => {
        getSyncProcessStatus(syncType);
      }, site.syncStatusTimeout);
    }
  }

  componentWillUnmount() {
    const { resetSyncStatus } = this.props;
    clearTimeout(this.timeoutGetSyncStatus);

    resetSyncStatus();
  }

  render() {
    const { syncType, showProgress } = this.state;
    let { lastSyncedDoneAt, i18n, flow, installSyncStatus } = this.props;
    const { updatedAt } = flow;

    // get last update time
    lastSyncedDoneAt =
      lastSyncedDoneAt.length > 10 ? parseInt(lastSyncedDoneAt) : parseInt(lastSyncedDoneAt) * 1000;
    const lastUpdateTimeString = updatedAt
      ? formatDatetime(updatedAt)
      : formatDatetime(lastSyncedDoneAt);

    return (
      <div className="BC-SyncHookStatus__DashboardV3">
        <Card sectioned>
          <Heading>{i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.cardTitle_Span')}</Heading>

          <div>
            <TextContainer>
              {installSyncStatus === 'running' ? (
                this.renderInstallSyncProgress(i18n)
              ) : syncType === SYNC_TYPE_FLOW_MANUAL_SYNC ? (
                !showProgress ? (
                  <React.Fragment>
                    {lastUpdateTimeString !== '' && (
                      <div style={{ marginBottom: '18px' }} className="recent-sync">
                        {i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.caption1')}{' '}
                        <TextStyle variation="strong">
                          {lastUpdateTimeString ? lastUpdateTimeString : 0}
                        </TextStyle>
                      </div>
                    )}
                    <div>
                      <Button onClick={this.goToSyncPage}>
                        {i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.buttonText')}
                      </Button>
                    </div>
                  </React.Fragment>
                ) : (
                  this.renderSyncProgress(i18n)
                )
              ) : (
                this.renderSpecialSyncProgress(i18n)
              )}
            </TextContainer>
          </div>
        </Card>
      </div>
    );
  }

  // Get current process when sync is running
  getCurrentProcess = i18n => {
    const { syncProcess, flow } = this.props;
    const { processName = '' } = syncProcess;
    const { status } = flow;

    if (status === 'done') {
      return (
        <TextStyle variation="positive">
          {i18n.translate('AdminHTML.Sync.Sync.getCurrentProcess_TextStyle')}
        </TextStyle>
      );
    } else if (processName) {
      let { processedCount = 0, totalCount = 0, completedPercent = 1, status } = syncProcess;

      const statusObject = _.find(SYNC_PROCESS_STATUSES, function ({ value }) {
        return value === status;
      });
      const statusLabel = statusObject ? i18n.translate(statusObject.label) : '';

      completedPercent = Math.round(completedPercent * 1000) / 10;
      completedPercent =
        completedPercent > 100 ? (completedPercent / 10).toFixed(1) : completedPercent;

      processedCount = parseFloat(processedCount).toFixed(2);

      return (
        <TextStyle variation="strong">
          {`${processName} - ${processedCount}/${totalCount} - ${completedPercent}% (${statusLabel})`}
        </TextStyle>
      );
    }
    return '';
  };

  // Render caption when sync is running
  renderCaptionProgress(i18n) {
    const { flow, triggerSync } = this.props;
    const { status, createdAt, updatedAt, flowName, restartAt } = flow;
    switch (status) {
      case 'wait-retry': {
        let timeRemain = Math.floor(Date.now() / 1000) - updatedAt;
        timeRemain = timeRemain > 0 ? timeRemain : 0;

        return (
          <p>
            {i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_WaitRetry', {
              nt_duration_in_minute: i18n.translate('AdminHTML.Common.Duration.shortMinute', {
                count: timeRemain,
                nt_number: i18n.formatNumber(timeRemain)
              })
            })}
          </p>
        );
      }
      case 'error': {
        let timeRemain = Math.floor(Date.now() / 1000) - restartAt;
        timeRemain = timeRemain > 0 ? timeRemain : 0;

        return (
          <p>
            {i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Error', {
              nt_duration_in_minute: i18n.translate('AdminHTML.Common.Duration.shortMinute', {
                count: timeRemain,
                nt_number: i18n.formatNumber(timeRemain)
              }),
              t1: (
                <Button plain onClick={() => triggerSync(SYNC_TYPE_FLOW_MANUAL_SYNC)}>
                  {i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Error_t1')}
                </Button>
              )
            })}
          </p>
        );
      }
      case 'done': {
        if (updatedAt) {
          return (
            <div>
              {_.isEmpty(flowName)
                ? i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Done_AutoSync', {
                    nt_time: <TextStyle variation="strong">{formatDatetime(updatedAt)}</TextStyle>
                  })
                : i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Done_Sync', {
                    nt_time: <TextStyle variation="strong">{formatDatetime(updatedAt)}</TextStyle>
                  })}
            </div>
          );
        }
        break;
      }
      default: {
        if (createdAt) {
          return (
            <div>
              {i18n.translate(
                'AdminHTML.DashboardV3.SyncHookStatus.renderCaptionProgress_Default_Sync',
                {
                  nt_time: <TextStyle variation="strong">{formatDatetime(createdAt)}</TextStyle>
                }
              )}
            </div>
          );
        }
      }
    }
    return '';
  }

  // Render progress of sync
  renderSyncProgress(i18n) {
    // const { i18n } = this.props;
    const { percentage } = this.state;

    return (
      <React.Fragment>
        <div className="progress-caption">{this.renderCaptionProgress(i18n)}</div>
        <div className="progress-status">
          <strong>
            {i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.percentToCompleteLabel', {
              nt_number: _.isInteger(percentage) ? percentage : percentage.toFixed(2)
            })}
          </strong>
          <ProgressBar
            size="medium"
            color="primary"
            progress={_.isInteger(percentage) ? percentage : percentage.toFixed(2)}
          />
        </div>
        <div className="progress-step-status">
          <img src={syncLoadingIcon} height="11" width="11" alt="sync-fif" />
          <span>{this.getCurrentProcess(i18n)}</span>
        </div>
      </React.Fragment>
    );
  }

  // Render progress of sync
  renderInstallSyncProgress(i18n) {
    const { installSyncStartedAt, installSyncRemainingTime } = this.props;
    const { percentage } = this.state;

    return (
      <React.Fragment>
        <div className="progress-caption">
          <div>
            {i18n.translate(
              'AdminHTML.DashboardV3.SyncHookStatus.renderCaptionProgress_Default_Sync',
              {
                nt_time: (
                  <TextStyle variation="strong">{formatDatetime(installSyncStartedAt)}</TextStyle>
                )
              }
            )}
          </div>
          <div>
            {i18n.translate(
              'AdminHTML.DashboardV3.SyncHookStatus.renderCaptionProgress_EstimateTime',
              {
                nt_time: (
                  <TextStyle variation="strong">
                    {formatSecondsToMinutesOrHours(installSyncRemainingTime, i18n)}
                  </TextStyle>
                )
              }
            )}
          </div>
        </div>
        <div className="progress-status">
          <strong>
            {i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.percentToCompleteLabel', {
              nt_number: _.isInteger(percentage) ? percentage : percentage.toFixed(2)
            })}
          </strong>
          <ProgressBar
            size="medium"
            color="primary"
            progress={_.isInteger(percentage) ? percentage : percentage.toFixed(2)}
          />
        </div>
      </React.Fragment>
    );
  }

  renderSpecialSyncProgress(i18n) {
    const { syncType, showProgress, syncProcessDot, triggerSyncFlag } = this.state;
    if (!syncType) {
      return '';
    }
    const syncContent = getSyncContent(syncType, i18n);
    const { flow, emailSupport } = this.props;
    let { status, remainingTime = 0 } = flow;

    switch (status) {
      case 'error': {
        return (
          <React.Fragment>
            <p>
              {i18n.translate('AdminHTML.Sync.Sync.renderSpecialSyncProgress_Error_Paragraph', {
                nt_process_type: syncContent.name,
                nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
              })}
            </p>
          </React.Fragment>
        );
      }
      case 'done': {
        if (showProgress && !triggerSyncFlag) {
          return (
            <React.Fragment>
              <div className="special-sync-banner special-sync-success">
                <img className="icon-success" src={SuccessIcon} alt="" />
                <span>{syncContent.completeText}</span>
              </div>
              <p>
                {i18n.translate('AdminHTML.Sync.Sync.renderSpecialSyncProgress_Done_Paragraph', {
                  nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
                })}
              </p>
            </React.Fragment>
          );
        }
        break;
      }
      default: {
        // do nothing
      }
    }

    return (
      <React.Fragment>
        <p>{syncContent.description}</p>
        <div className="button-sync">
          {showProgress && (
            <div>
              {i18n.translate('AdminHTML.Sync.Sync.renderSpecialSyncProgress_ShowProcess_Text1', {
                nt_syncing_type: syncContent.name,
                nt_duration: getTimeConvert(remainingTime, i18n),
                nt_three_dots: syncProcessDot
              })}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  // Go to sync page
  goToSyncPage = () => {
    history.push(routes.sync.view());
  };
}

const mapStateToProps = (state, props) => {
  return {
    tenantId: _.get(state.oauth.tenantApp, 'tenantId', ''),
    error: _.get(state.sync, 'error', undefined),
    lastSyncedDoneAt: _.get(state.sync, 'lastSyncedDoneAt', '0'),
    triggerSyncLoading: _.get(state.sync, 'triggerSyncLoading', false),
    syncProcessLoading: _.get(state.sync, 'syncProcessLoading', false),
    flow: _.get(state.sync, 'flow', {}),
    syncProcess: _.get(state.sync, 'syncProcess', {}),
    availableSyncList: _.get(state.sync, 'availableSyncList', []),
    collectionBoostAllExists: _.get(state.sync, 'collectionBoostAllExists', false),
    installSyncStatus: _.get(state.install, 'syncStatus'),
    installSyncProgress: _.get(state.install, 'syncProgress'),
    installSyncStartedAt: _.get(state.install, 'syncStartedAt'),
    installSyncRemainingTime: _.get(state.install, 'syncRemainingTime', 0)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get sync process status
  getSyncProcessStatus: (flowName = '') => dispatch(syncActions.getSyncProcessStatus(flowName)),

  // Get sync status
  getSyncStatus: tenantId => dispatch(syncActions.getSyncStatus(tenantId)),
  resetSyncStatus: () => dispatch(syncActions.resetSyncStatus()),
  triggerSync: flowName => dispatch(syncActions.triggerSync(flowName, ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SyncHookStatus));
